import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronRight } from "lucide-react";
import { NAV_LINKS, BUSINESS } from "@/lib/business-info";

export default function Nav() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            data-testid="site-nav"
            className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-charcoal/85 backdrop-blur-md border-b border-cream/10"
                    : "bg-gradient-to-b from-charcoal/70 via-charcoal/30 to-transparent backdrop-blur-[2px]"
            }`}
        >
            <nav className="empire-container flex items-center justify-between h-24 md:h-32">
                {/* Logo wordmark */}
                <Link
                    to="/"
                    data-testid="nav-logo"
                    className="group flex items-center"
                    aria-label="Empire Jamaica Fusion — Home"
                >
                    <img
                        src="/brand/empire-plaque-240.png"
                        srcSet="/brand/empire-plaque-120.png 1x, /brand/empire-plaque-240.png 2x, /brand/empire-plaque-360.png 3x"
                        alt="Empire Jamaica Fusion"
                        width="172"
                        height="96"
                        decoding="sync"
                        fetchpriority="high"
                        className="h-[58px] md:h-[88px] w-auto select-none transition-transform duration-300 group-hover:scale-[1.03]"
                    />
                </Link>

                {/* Desktop links */}
                <ul className="hidden lg:flex items-center gap-9">
                    {NAV_LINKS.map((l) => (
                        <li key={l.path}>
                            <NavLink
                                to={l.path}
                                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={({ isActive }) =>
                                    `text-[11px] uppercase tracking-[0.24em] font-medium transition-colors ${
                                        isActive
                                            ? "text-gold"
                                            : "text-cream/70 hover:text-gold"
                                    }`
                                }
                                end={l.path === "/"}
                            >
                                {l.label}
                            </NavLink>
                        </li>
                    ))}
                </ul>

                {/* Desktop CTA */}
                <div className="hidden lg:flex items-center gap-4">
                    <a
                        href={BUSINESS.phoneLink}
                        data-testid="nav-phone"
                        className="text-[11px] uppercase tracking-[0.22em] text-cream/70 hover:text-gold transition-colors"
                    >
                        {BUSINESS.phoneDisplay}
                    </a>
                    <Link
                        to="/#catering-inquiry"
                        onClick={(e) => {
                            if (location.pathname === "/") {
                                e.preventDefault();
                                document
                                    .getElementById("catering-inquiry")
                                    ?.scrollIntoView({ behavior: "smooth" });
                            }
                        }}
                        data-testid="nav-cta-book-catering"
                        className="empire-btn-primary !py-3 !px-6 !text-[11px]"
                    >
                        Book Catering
                    </Link>
                </div>

                {/* Mobile toggle */}
                <button
                    type="button"
                    data-testid="nav-mobile-toggle"
                    aria-label={open ? "Close menu" : "Open menu"}
                    aria-expanded={open}
                    onClick={() => setOpen((v) => !v)}
                    className="lg:hidden inline-flex h-10 w-10 items-center justify-center text-cream border border-cream/15 hover:border-gold hover:text-gold transition-colors"
                >
                    {open ? <X size={18} /> : <Menu size={18} />}
                </button>
            </nav>

            {/* Mobile drawer */}
            <div
                data-testid="nav-mobile-panel"
                className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-500 ease-out border-t border-cream/5 ${
                    open
                        ? "max-h-[80vh] opacity-100"
                        : "max-h-0 opacity-0 pointer-events-none"
                } bg-charcoal/95 backdrop-blur-md`}
            >
                <div className="empire-container py-6">
                    <ul className="flex flex-col divide-y divide-cream/5">
                        {NAV_LINKS.map((l) => (
                            <li key={l.path}>
                                <NavLink
                                    to={l.path}
                                    data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    className={({ isActive }) =>
                                        `flex items-center justify-between py-4 text-[13px] uppercase tracking-[0.24em] ${
                                            isActive
                                                ? "text-gold"
                                                : "text-cream/85"
                                        }`
                                    }
                                    end={l.path === "/"}
                                >
                                    <span>{l.label}</span>
                                    <ChevronRight
                                        size={16}
                                        strokeWidth={1.5}
                                        className="text-gold/50 shrink-0"
                                        aria-hidden="true"
                                    />
                                </NavLink>
                            </li>
                        ))}
                    </ul>
                    <div className="mt-6 grid grid-cols-2 gap-3">
                        <a
                            href={BUSINESS.phoneLink}
                            data-testid="mobile-nav-phone"
                            className="empire-btn-secondary !py-3 !text-[11px]"
                        >
                            Call
                        </a>
                        <Link
                            to="/#catering-inquiry"
                            data-testid="mobile-nav-cta"
                            className="empire-btn-primary !py-3 !text-[11px]"
                        >
                            Book Catering
                        </Link>
                    </div>
                </div>
            </div>
        </header>
    );
}
